import type { Workspace } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { getReleaseActionGating } from './releaseActionGating';

export const MAX_BULK_PROMOTE_WORKSPACES = 5;

export interface BulkPromoteEligibilityResult {
  eligible: boolean;
  /** Standalone sentence fragment for a disabled checkbox's tooltip — unset when eligible. */
  reason?: string;
}

/**
 * Whether `candidate` can join (or continue to belong to) a bulk
 * promote/upgrade selection alongside `selected` (which must exclude
 * `candidate` itself) — the same constraints PromoteReleaseMulti enforces
 * server-side (max 5, same deployment, same current_release_id), checked
 * client-side so a checkbox disables itself with an explanation instead of
 * letting the user select then fail on submit.
 */
export function getBulkPromoteEligibility(
  candidate: Workspace,
  selected: Workspace[],
): BulkPromoteEligibilityResult {
  const gating = getReleaseActionGating(candidate, {
    noCurrentRelease: !candidate.currentReleaseId,
  });
  if (gating.disabled) {
    return {
      eligible: false,
      reason: candidate.currentReleaseId
        ? 'This workspace already has an upgrade in progress.'
        : 'This workspace has no current release recorded yet.',
    };
  }
  if (selected.length === 0) {
    return { eligible: true };
  }
  if (selected.length >= MAX_BULK_PROMOTE_WORKSPACES) {
    return {
      eligible: false,
      reason: `You can select at most ${MAX_BULK_PROMOTE_WORKSPACES} workspaces at once — ${MAX_BULK_PROMOTE_WORKSPACES} are already selected.`,
    };
  }
  const first = selected[0];
  if (!first) {
    return { eligible: true };
  }
  if (first.deploymentId !== candidate.deploymentId) {
    return {
      eligible: false,
      reason: first.deploymentDisplayName
        ? `Selected workspaces are in the "${first.deploymentDisplayName}" deployment; this workspace is in a different one.`
        : 'This workspace is in a different deployment than the ones already selected.',
    };
  }
  if (first.currentReleaseId !== candidate.currentReleaseId) {
    return {
      eligible: false,
      reason: `Selected workspaces are on v${first.currentVersion || 'unknown'}; this workspace is on v${candidate.currentVersion || 'unknown'}. All selected workspaces must be on the same release.`,
    };
  }
  return { eligible: true };
}
